import { array } from 'fp-ts';
import { forwardRef } from 'react';
import { TimeUnitPeriod } from '@mews-commander/data-access';
import { CalendarEvent, WithTimeUnit } from '@mews-commander/types';
import { CalendarEventList } from '@mews-commander/ui';
import { TimeUnit } from '@mews/enums';
import {
    DateTime,
    DateTimeService,
    LocalizedDate,
    LocalizedDayName,
    LocalizedMonthName,
    LocalizedShortTime,
} from '@mews/framework';
import { Badge, formatCountToMaxNine, Stack, Tooltip, Typography } from '@optimus-web/core';

interface TimeUnitTextProps extends WithTimeUnit {
    date: DateTime;
    isDayView: boolean;
}

interface TimeUnitInfoContentProps extends WithTimeUnit {
    calendarEvents?: Array<CalendarEvent>;
    date: DateTime;
    timeUnitPeriod?: TimeUnitPeriod;
}

const PrimaryTimeUnitText = ({ timeUnit, date, isDayView }: TimeUnitTextProps) => {
    if (isDayView) return <LocalizedDayName date={date} />;

    switch (timeUnit) {
        case TimeUnit.Hour:
            return <LocalizedShortTime dateTime={date} />;
        case TimeUnit.Month:
            return <LocalizedMonthName dateTime={date} />;
        default:
            return <LocalizedDayName date={date} />;
    }
};

const SecondaryTimeUnitText = ({ timeUnit, date, isDayView }: TimeUnitTextProps) => {
    if (isDayView) return <LocalizedDate date={date} />;

    switch (timeUnit) {
        case TimeUnit.Month:
            return <>{DateTimeService.getYear(date)}</>;
        default:
            return <LocalizedDate date={date} />;
    }
};

const TimeUnitInfoContent = forwardRef(
    (
        { calendarEvents, date, timeUnit = TimeUnit.Day, timeUnitPeriod, ...other }: TimeUnitInfoContentProps,
        ref: React.Ref<HTMLDivElement>
    ) => {
        const isDayView = timeUnit === TimeUnit.Month && timeUnitPeriod === TimeUnitPeriod.Day;

        return (
            <Stack spacing="200" ref={ref} {...other}>
                <Stack fill vertical>
                    <Typography textStyle="bodySmallStrong" as="div">
                        <PrimaryTimeUnitText timeUnit={timeUnit} date={date} isDayView={isDayView} />
                    </Typography>
                    <Typography textStyle="bodySmall" variant="secondary" as="div">
                        <SecondaryTimeUnitText timeUnit={timeUnit} date={date} isDayView={isDayView} />
                    </Typography>
                </Stack>
                {calendarEvents !== undefined && calendarEvents.length > 0 ? (
                    <Badge content={formatCountToMaxNine(calendarEvents.length)} />
                ) : null}
            </Stack>
        );
    }
);

const TooltippedTimeUnitInfoContent = Tooltip(TimeUnitInfoContent);

const TimeUnitInfo = ({ calendarEvents, ...other }: TimeUnitInfoContentProps) => (
    <TooltippedTimeUnitInfoContent
        calendarEvents={calendarEvents}
        tooltip={
            calendarEvents && array.isNonEmpty(calendarEvents) ? (
                <CalendarEventList calendarEvents={calendarEvents} />
            ) : null
        }
        {...other}
    />
);

export default TimeUnitInfo;
